import { z } from 'zod/v4';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';

import {
    AxisConfigSchema,
    BaseVisualizationArgsSchema,
    ColumnReferenceSchema,
    createColumnTransform,
    createTypedColumnTransform,
    createVisualizationResponseSchema,
    ExecutionMetadata,
    LegendConfigSchema,
    TooltipConfigSchema,
    VisualizationParametersSchema,
} from '../validation/schemas';

const AggregatorFunctionSchema = z.enum(['sum', 'avg', 'min', 'max', 'first', 'last']);
const SeriesTypeSchema = z.enum(['bar', 'line', 'area', 'scatter']);
const FormatSchema = z.enum(['number', 'usd', 'percent', 'usdShort']);

// ComputedSeriesExpression schema for recursive expression validation
const ComputedSeriesExpressionSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
        operator: z.enum(['+', '-', '*', '/']),
        left: z.union([z.string(), ComputedSeriesExpressionSchema]),
        right: z.union([z.string(), ComputedSeriesExpressionSchema]),
    }),
);

// Base schema for common series properties
const AbstractSeriesConfigSchema = z.object({
    type: SeriesTypeSchema.optional(),
    axis: AxisConfigSchema.shape.orientation.optional(),
    format: FormatSchema.optional(),
    summary: AggregatorFunctionSchema.optional(),
    color: z.string(),
    label: z.string(),
});

// Regular series config
const RegularSeriesConfigSchema = AbstractSeriesConfigSchema.extend({
    column: ColumnReferenceSchema, // Will be transformed
    computed: z.literal(false).optional(),
    expression: z.never().optional(),
}).strip();

// Computed series config
const ComputedSeriesConfigSchema = AbstractSeriesConfigSchema.extend({
    column: z.string(), // Will be transformed
    computed: z.literal(true)?.optional(),
    expression: ComputedSeriesExpressionSchema.optional(),
}).strip();

// Union of regular and computed series
export const SeriesConfigSchema = z.union([RegularSeriesConfigSchema, ComputedSeriesConfigSchema]);

export const GroupConfigSchema = z
    .object({
        id: z.string().optional(),
        name: z.string().optional(),
        palette: z.enum(['brand', 'base', 'vibrantMix', 'modernDashboard', 'softSpectrum']).optional(),
        summary: AggregatorFunctionSchema.optional(),
        type: SeriesTypeSchema.optional(),
        transform: z.enum(['cumulative', 'percent', 'none']).optional(),
        synthetic: z.string().optional(),
        labelFormat: z.enum(['none', 'startCase']).optional(),
        seriesStrategy: z.enum(['dynamic', 'manual']).optional(),
        stack: z.boolean().optional(),
        axis: AxisConfigSchema.extend({
            format: FormatSchema,
        }),
        series: z.array(SeriesConfigSchema),
    })
    .strip();

const XAxisConfigSchema = z
    .object({
        type: z.enum(['date', 'enum']).optional(),
        column: z.string(),
        orderBy: z.string().optional(),
        orderDirection: z.enum(['asc', 'desc']).optional(),
        title: z.string().optional(),
        logarithmic: z.boolean().optional().default(false),
        groupBy: z.string().optional().nullable(),
        tickStrategy: z.enum(['auto', 'all']).optional(),
        format: z.enum(['hourly', 'daily', 'weekly', 'monthly', 'quarterly']).optional(),
    })
    .strip();

// Extend the shared TooltipConfigSchema with timeseries-specific fields
const TimeseriesToolTipConfigSchema = TooltipConfigSchema.extend({
    showTotal: z.boolean().optional(),
    autoSort: z.boolean().optional(),
});

export const TimeseriesOptionsSchema = z
    .object({
        legend: LegendConfigSchema.optional(),
        xAxis: XAxisConfigSchema,
        brush: z.boolean().optional(),
        logarithmic: z.boolean().optional(),
        strokeWidth: z.string().optional(),
        groups: z.array(GroupConfigSchema),
        tooltip: TimeseriesToolTipConfigSchema.optional(),
        searchableLegend: z.boolean().optional(),
        hideAllSeries: z.boolean().optional(),
    })
    .strip();

export const createTimeseriesArgsSchema = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    const columnTransform = createColumnTransform(meta);
    const numericColumnTransform = createTypedColumnTransform(meta, 'number');
    // ComputedSeriesExpression schema for recursive expression validation
    const expressionTransform = z.object({
        operator: z.enum(['+', '-', '*', '/']),
        left: z.union([numericColumnTransform, z.lazy<any>(() => expressionTransform)]),
        right: z.union([numericColumnTransform, z.lazy<any>(() => expressionTransform)]),
    });

    // Transform the full options schema

    return BaseVisualizationArgsSchema.extend({
        type: z.literal(StudioVisualizationType.Timeseries),
        config: z.object({
            id: z.string().optional(),
            parameters: VisualizationParametersSchema.optional(),
            options: TimeseriesOptionsSchema.extend({
                xAxis: XAxisConfigSchema.extend({
                    column: columnTransform,
                    orderBy: columnTransform.optional(),
                    groupBy: z.preprocess(
                        val => ([null, 'none', undefined].includes(val as string) ? undefined : val),
                        columnTransform.optional().nullable(),
                    ),
                }),
                groups: z.array(
                    z.preprocess(
                        (group: z.infer<typeof GroupConfigSchema>) => {
                            // This is a special legacy case where dynamic series are predefined in the config and need to be removed to avoid validation errors
                            if (group.seriesStrategy === 'dynamic' && group.synthetic) {
                                return { ...group, series: [], source: 'computed' };
                            }
                            if (group.synthetic) {
                                return { ...group, source: 'computed' };
                            }
                            return {
                                ...group,
                                source: 'metadata',
                            };
                        },
                        z.discriminatedUnion('source', [
                            GroupConfigSchema.extend({
                                source: z.literal('metadata'),
                                synthetic: z.string().optional(), // Will be transformed
                                series: z.array(
                                    z.union([
                                        ComputedSeriesConfigSchema.extend({
                                            expression: expressionTransform,
                                        }),
                                        RegularSeriesConfigSchema.extend({
                                            column: numericColumnTransform,
                                        }),
                                    ]),
                                ),
                            }),
                            GroupConfigSchema.extend({
                                source: z.literal('computed'),
                                synthetic: columnTransform,
                                series: z.array(RegularSeriesConfigSchema).default([]),
                            }),
                        ]),
                    ),
                ),
            }),
        }),
    });
};

export const TimeseriesResponseSchema = createVisualizationResponseSchema(StudioVisualizationType.Timeseries).extend({
    options: TimeseriesOptionsSchema,
    data: z.any(),
});

export type TimeseriesOptions = z.infer<typeof TimeseriesOptionsSchema>;
export type SeriesConfig = z.infer<typeof SeriesConfigSchema>;
export type GroupConfig = z.infer<typeof GroupConfigSchema>;
export type TimeseriesResponse = z.infer<typeof TimeseriesResponseSchema>;

// Export the generic type for the args schema
export type TimeseriesArgsSchema<TColumns extends string = string> = ReturnType<
    typeof createTimeseriesArgsSchema<TColumns>
>;
export type TimeseriesArgs<TColumns extends string = string> = z.infer<TimeseriesArgsSchema<TColumns>>;
